/**
 * The `/content` screen's decision layer (SPEC `Content Performance` § Frontend).
 *
 * Everything the screen could get wrong lives here as pure functions, for the reason every other
 * plan module exists: the web suites run in `node` with no DOM, so what can be tested is the plan,
 * not the markup. `ContentPage`, `ContentMetrics`, the two tables and `ImportDialog` render this —
 * they decide nothing.
 *
 * Four things it owns, and why each is not obvious:
 *
 *  - **The persisted blob** (`content:main`). One global blob, not per subject: this screen has no
 *    subject. Its defaults are the spec's, and they are *not* the view's — All time rather than 30
 *    days (an import is a 90-day window that only makes sense whole) and Weekly rather than the
 *    server's auto-pick (the only granularity where every Overview series renders, decision 14).
 *  - **`?mode=` beats the stored mode** (§12.4), and only an explicit param does. Restoring happens
 *    once, before the first data query, and never fights a link someone pasted.
 *  - **Series availability is a server discriminant.** A series that cannot exist at the requested
 *    granularity ships `{available:false, reasonDisplay}`; the plan hands the chart nothing for it
 *    and the stat cell the server's sentence. Nothing here decides *why* — it only routes.
 *  - **The import is a four-outcome request.** 200, a `409` handle clash that persisted nothing and
 *    must be re-submitted with `confirm=1`, a hard `400`, and everything else. The reducer keeps
 *    those apart so the dialog never re-posts a file the server already accepted, and never asks
 *    for a confirmation it has already been given.
 *  - **The stored column layout is sanitised here**, sentinel and all, so the re-seed rule is a
 *    node-tested function rather than a branch buried in a cache-backed hook.
 */

import {
  BUCKET_GRANULARITIES,
  CONTENT_MAX_UPLOAD_BYTES,
  CONTENT_MODES,
  CONTENT_COMMENTS_SORT_KEYS,
  CONTENT_POSTS_SORT_KEYS,
  DEFAULT_CONTENT_COMMENTS_SORT,
  DEFAULT_CONTENT_POSTS_SORT,
  RANGE_PRESETS,
  SORT_DIRS,
  type BucketGranularity,
  type ContentCommentAuthorRowDTO,
  type ContentCommentsQuery,
  type ContentCommentsSortKey,
  type ContentHandleMismatchDetails,
  type ContentImportResponse,
  type ContentMetricsQuery,
  type ContentMode,
  type ContentPostsQuery,
  type ContentPostsSortKey,
  type ContentSeriesDTO,
  type ContentUiState,
  type RangePreset,
  type SortDir,
  type ViewRangeState,
} from '@lw/shared';
import { pinColumnFirst, type ResolvedRange } from '../view/viewState';

export type ContentPostsSort = { key: ContentPostsSortKey; dir: SortDir };
export type ContentCommentsSort = { key: ContentCommentsSortKey; dir: SortDir };

export type ContentState = {
  /** Absent until the user has switched modes at least once (§12.4 restores only what exists). */
  mode?: ContentMode;
  range: ViewRangeState;
  granularity: BucketGranularity;
  postsSort: ContentPostsSort;
  commentsSort: ContentCommentsSort;
};

/**
 * SPEC decisions 14 and 17: All time, Weekly. Fresh literals per field, never one shared object —
 * the two sorts diverge the moment either is set.
 */
export const DEFAULT_CONTENT_STATE: ContentState = {
  range: { preset: 'all' },
  granularity: 'week',
  postsSort: { key: DEFAULT_CONTENT_POSTS_SORT.key, dir: DEFAULT_CONTENT_POSTS_SORT.dir },
  commentsSort: {
    key: DEFAULT_CONTENT_COMMENTS_SORT.key,
    dir: DEFAULT_CONTENT_COMMENTS_SORT.dir,
  },
};

/** Mode-switcher labels and the granularity menu's — chrome words, not server strings. */
export const CONTENT_MODE_LABEL: Record<ContentMode, string> = {
  metrics: 'Metrics',
  posts: 'Posts',
  comments: 'Comments',
};

export const CONTENT_GRANULARITY_LABEL: Record<BucketGranularity, string> = {
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly',
};

/* ---------------------------------------------------------------- sanitize */

function pickEnum<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' ? allowed.find((candidate) => candidate === value) : undefined;
}

function pickNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** The shared range vocabulary, re-validated here so a hand-edited blob cannot reach a query. */
export function sanitizeContentRange(raw: unknown): ViewRangeState {
  if (typeof raw !== 'object' || raw === null) return { ...DEFAULT_CONTENT_STATE.range };
  const source = raw as Record<string, unknown>;
  const preset = pickEnum<RangePreset>(source.preset, RANGE_PRESETS);
  if (preset === undefined) return { ...DEFAULT_CONTENT_STATE.range };
  if (preset !== 'custom') return { preset };
  const from = pickNumber(source.from);
  const to = pickNumber(source.to);
  if (from === undefined || to === undefined || to < from) {
    return { ...DEFAULT_CONTENT_STATE.range };
  }
  return { preset, from, to };
}

function sanitizeSort<K extends string>(
  raw: unknown,
  keys: readonly K[],
  fallback: { key: K; dir: SortDir },
): { key: K; dir: SortDir } {
  if (typeof raw !== 'object' || raw === null) return { ...fallback };
  const source = raw as Record<string, unknown>;
  const key = pickEnum<K>(source.key, keys);
  const dir = pickEnum<SortDir>(source.dir, SORT_DIRS);
  if (key === undefined || dir === undefined) return { ...fallback };
  return { key, dir };
}

/** Stored UI state is user-writable JSON: never trust its shape, always fall back to defaults. */
export function sanitizeContentState(raw: unknown): ContentState {
  if (typeof raw !== 'object' || raw === null) {
    return {
      range: { ...DEFAULT_CONTENT_STATE.range },
      granularity: DEFAULT_CONTENT_STATE.granularity,
      postsSort: { ...DEFAULT_CONTENT_STATE.postsSort },
      commentsSort: { ...DEFAULT_CONTENT_STATE.commentsSort },
    };
  }
  const source = raw as Record<string, unknown>;
  const mode = pickEnum<ContentMode>(source.mode, CONTENT_MODES);
  const state: ContentState = {
    range: sanitizeContentRange(source.range),
    granularity:
      pickEnum<BucketGranularity>(source.granularity, BUCKET_GRANULARITIES) ??
      DEFAULT_CONTENT_STATE.granularity,
    postsSort: sanitizeSort<ContentPostsSortKey>(
      source.postsSort,
      CONTENT_POSTS_SORT_KEYS,
      DEFAULT_CONTENT_STATE.postsSort,
    ),
    commentsSort: sanitizeSort<ContentCommentsSortKey>(
      source.commentsSort,
      CONTENT_COMMENTS_SORT_KEYS,
      DEFAULT_CONTENT_STATE.commentsSort,
    ),
  };
  if (mode !== undefined) state.mode = mode;
  return state;
}

/**
 * What actually goes to `PUT /api/ui-state/content:main` (the shape of `ContentUiStateSchema`).
 * A strict whitelist: a field missing here is silently dropped on every write.
 */
export function toStoredContentState(state: ContentState): ContentUiState {
  const stored: ContentUiState = {
    range: state.range,
    granularity: state.granularity,
    postsSort: state.postsSort,
    commentsSort: state.commentsSort,
  };
  if (state.mode !== undefined) stored.mode = state.mode;
  return stored;
}

/* -------------------------------------------------------------------- mode */

export type ContentModeRestore = {
  /** The mode the URL currently resolves to (the default when `?mode=` is absent/garbage). */
  urlMode: ContentMode;
  /** `true` when `?mode=` is present in the URL at all — a garbage value still counts. */
  hasExplicitMode: boolean;
  /** What the persisted blob holds, if anything. */
  storedMode: ContentMode | undefined;
};

/**
 * §12.4: an explicit `?mode=` **overrides** the persisted mode; without one the stored mode is
 * restored. Returns the mode to switch to, or `null` when the URL already stands.
 */
export function restoredContentMode(input: ContentModeRestore): ContentMode | null {
  if (input.hasExplicitMode) return null;
  if (input.storedMode === undefined || input.storedMode === input.urlMode) return null;
  return input.storedMode;
}

/* ------------------------------------------------------------------ params */

/** #87 params. `all` sends `from = 0` rather than omitting it, exactly as the view screen does. */
export function toContentMetricsParams(
  state: ContentState,
  resolved: ResolvedRange,
): Partial<ContentMetricsQuery> {
  return { from: resolved.from, to: resolved.to, granularity: state.granularity };
}

function trimmedSearch(search: string): string | undefined {
  const trimmed = search.trim();
  return trimmed.length > 0 ? trimmed.slice(0, 200) : undefined;
}

/** #88 params, minus the window (`offset`/`limit`), which belongs to the virtualizer. */
export function toContentPostsParams(
  state: ContentState,
  resolved: ResolvedRange,
  search: string,
): Partial<ContentPostsQuery> {
  const params: Partial<ContentPostsQuery> = {
    from: resolved.from,
    to: resolved.to,
    sort: state.postsSort.key,
    dir: state.postsSort.dir,
  };
  const q = trimmedSearch(search);
  if (q !== undefined) params.q = q;
  return params;
}

/** #89 params — the same set against the comments vocabulary. */
export function toContentCommentsParams(
  state: ContentState,
  resolved: ResolvedRange,
  search: string,
): Partial<ContentCommentsQuery> {
  const params: Partial<ContentCommentsQuery> = {
    from: resolved.from,
    to: resolved.to,
    sort: state.commentsSort.key,
    dir: state.commentsSort.dir,
  };
  const q = trimmedSearch(search);
  if (q !== undefined) params.q = q;
  return params;
}

/**
 * A header click on a sortable column: same key flips the direction, a new key starts at the
 * direction that reads first for its kind (dates, metrics and rates descend, text ascends).
 */
export function nextContentSort<K extends string>(
  current: { key: K; dir: SortDir },
  key: K,
  kind: string,
): { key: K; dir: SortDir } {
  if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  const descFirst =
    kind === 'date' ||
    kind === 'metric' ||
    kind === 'multiplier' ||
    kind === 'share' ||
    kind === 'rate';
  return { key, dir: descFirst ? 'desc' : 'asc' };
}

/* -------------------------------------------------------- stored column layout */

export type ContentColumnLayoutConfig<Id extends string> = {
  /** The `ui_state` key this layout is stored under. */
  storageKey: string;
  /** Canonical order — the registry's own array order. */
  ids: readonly Id[];
  defaultVisible: readonly Id[];
  /**
   * A column id from the registry's latest **reordering**. A stored blob whose order does not name
   * it predates that reordering, so honouring it would leave the shipped order unreachable — the
   * default order and visibility are re-seeded instead. A blob written afterwards is honoured
   * verbatim, so a user's own layout is never re-seeded twice.
   *
   * Only a *move* needs a new sentinel. A column merely *added* does not: an id the stored order
   * lacks is seated beside its registry neighbour and stays visible, so a customised layout
   * survives untouched — but a known id the order already names keeps its stored seat, which is
   * exactly what a move has to overrule.
   */
  sentinel: Id;
  /**
   * The frozen column, always seated first. Enforced on **every read and write**, independent of
   * the sentinel — pinning a column is a normalisation of whatever layout is stored, not a re-seed,
   * so no sentinel bump is needed and a custom order keeps everything else where the user put it.
   */
  pinned?: Id;
};

export type ContentStoredLayout<Id extends string> = { order: Id[]; hidden: Id[] };

export function contentColumnLayoutDefaults<Id extends string>(
  config: ContentColumnLayoutConfig<Id>,
): ContentStoredLayout<Id> {
  return {
    order: pinColumnFirst(config.ids, config.pinned ?? null),
    hidden: config.ids.filter((id) => !config.defaultVisible.includes(id)),
  };
}

function sanitizeColumnIds<Id extends string>(value: unknown, known: ReadonlySet<string>): Id[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const ids: Id[] = [];
  for (const entry of value) {
    if (typeof entry !== 'string' || !known.has(entry) || seen.has(entry)) continue;
    seen.add(entry);
    ids.push(entry as Id);
  }
  return ids;
}

/**
 * Seat every canonical id the stored order lacks after the nearest registry id *preceding* it that
 * the stored order does contain — walking the registry in order keeps that cursor correct wherever
 * the user dragged things. An id with no such predecessor present goes to the end.
 */
function seatUnknownIds<Id extends string>(
  storedOrder: readonly Id[],
  canonical: readonly Id[],
): Id[] {
  const order = [...storedOrder];
  let cursor: number | null = null; // index just past the last canonical id found in `order`
  for (const id of canonical) {
    const at = order.indexOf(id);
    if (at !== -1) {
      cursor = at + 1;
      continue;
    }
    const insertAt: number = cursor ?? order.length; // no predecessor present → the end
    order.splice(insertAt, 0, id);
    cursor = insertAt + 1;
  }
  return order;
}

/** Stored order verbatim, with any column it never heard of seated beside its registry neighbour. */
export function sanitizeContentColumnLayout<Id extends string>(
  raw: unknown,
  config: ContentColumnLayoutConfig<Id>,
): ContentStoredLayout<Id> {
  const fallback = contentColumnLayoutDefaults(config);
  if (typeof raw !== 'object' || raw === null) return fallback;
  const known = new Set<string>(config.ids);
  const source = raw as Record<string, unknown>;
  const storedOrder = sanitizeColumnIds<Id>(source.order, known);
  if (!storedOrder.includes(config.sentinel)) return fallback;
  return {
    // The sentinel branch above returns `fallback`, which is pin-first by construction.
    order: pinColumnFirst(seatUnknownIds(storedOrder, config.ids), config.pinned ?? null),
    hidden:
      source.hidden === undefined ? fallback.hidden : sanitizeColumnIds<Id>(source.hidden, known),
  };
}

/* ------------------------------------------------------ series availability */

/**
 * The last-resort sentence for a series the server sent nothing usable for. Client copy only ever
 * fills a hole the server left — it never rephrases a sentence the server did send (§12.5).
 */
export const CONTENT_SERIES_ABSENT_FALLBACK = 'Not present in any import.';

/** Static label for LinkedIn's own `partial` flag; the DTO ships the boolean, not the words. */
export const CONTENT_PARTIAL_LABEL = 'Partial week';

export type ContentSeriesPlan =
  | {
      available: true;
      values: readonly (number | null)[];
      /** `null` when the series carries no partial flags at all (every daily series). */
      partial: readonly boolean[] | null;
    }
  | { available: false; reasonDisplay: string };

/** Routes the server's `{available}` discriminant; decides nothing about *why* (§12.5 rule 5). */
export function contentSeriesPlan(series: ContentSeriesDTO | undefined): ContentSeriesPlan {
  if (series === undefined) {
    return { available: false, reasonDisplay: CONTENT_SERIES_ABSENT_FALLBACK };
  }
  if (!series.available) {
    return {
      available: false,
      reasonDisplay:
        series.reasonDisplay.length > 0 ? series.reasonDisplay : CONTENT_SERIES_ABSENT_FALLBACK,
    };
  }
  return {
    available: true,
    values: series.values,
    partial: series.partial === undefined ? null : series.partial,
  };
}

/** Toggling a stat cell: the set of active series keys, order-stable. */
export function toggleContentSeries<K extends string>(active: readonly K[], key: K): K[] {
  return active.includes(key) ? active.filter((entry) => entry !== key) : [...active, key];
}

/**
 * What actually gets plotted: a key the user turned on **and** the server can supply. An
 * unavailable series is never zero-filled onto the chart — its cell carries the sentence instead.
 */
export function plottedContentSeries<K extends string>(
  active: readonly K[],
  plans: Readonly<Record<K, ContentSeriesPlan>>,
  order: readonly K[],
): K[] {
  return order.filter((key) => active.includes(key) && plans[key].available);
}

/* ---------------------------------------- accounts-you-commented-on table */

export type ContentAuthorsSortKey =
  | 'name'
  | 'comments'
  | 'impressions'
  | 'impressionsX'
  | 'medianImpressions'
  | 'medianImpressionsX';

export type ContentAuthorsSort = { key: ContentAuthorsSortKey; dir: SortDir };

/** The server already ships the rows in this order, so the first paint sorts nothing. */
export const DEFAULT_CONTENT_AUTHORS_SORT: ContentAuthorsSort = { key: 'comments', dir: 'desc' };

/** The one text column; every other key sorts on a number and starts descending. */
const CONTENT_AUTHORS_TEXT_KEYS: readonly ContentAuthorsSortKey[] = ['name'];

function authorSortValue(
  row: ContentCommentAuthorRowDTO,
  key: ContentAuthorsSortKey,
): number | null {
  switch (key) {
    case 'comments':
      return row.comments.value;
    case 'impressions':
      return row.impressions.value;
    case 'impressionsX':
      return row.impressionsX.value;
    case 'medianImpressions':
      return row.medianImpressions.value;
    case 'medianImpressionsX':
      return row.medianImpressionsX.value;
    case 'name':
      return null;
  }
}

/**
 * The `PerMemberTable.sortRows` comparator on this table's rows: ordering reads `Explained.value`
 * and never a display string, a "—" cell sinks in **both** directions (a column you sorted by must
 * surface the accounts that actually report it), and name then key break every remaining tie.
 */
export function sortContentAuthors(
  rows: readonly ContentCommentAuthorRowDTO[],
  sort: ContentAuthorsSort,
): ContentCommentAuthorRowDTO[] {
  const factor = sort.dir === 'asc' ? 1 : -1;
  const textual = CONTENT_AUTHORS_TEXT_KEYS.includes(sort.key);
  return [...rows].sort((a, b) => {
    let result: number;
    if (textual) {
      result = a.name.localeCompare(b.name);
    } else {
      const left = authorSortValue(a, sort.key);
      const right = authorSortValue(b, sort.key);
      if (left === null || right === null) {
        if (left !== right) return left === null ? 1 : -1;
        result = 0;
      } else {
        result = left - right;
      }
    }
    if (result !== 0) return result * factor;
    return a.name.localeCompare(b.name) || a.key.localeCompare(b.key);
  });
}

/** First click: text ascending, numbers descending (the homescreen convention). */
export function nextContentAuthorsSort(
  current: ContentAuthorsSort,
  key: ContentAuthorsSortKey,
): ContentAuthorsSort {
  if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: CONTENT_AUTHORS_TEXT_KEYS.includes(key) ? 'asc' : 'desc' };
}

/* --------------------------------------------------------- import dialog */

export type ContentImportPhase = 'idle' | 'ready' | 'uploading' | 'confirm' | 'done' | 'error';

export type ContentImportState = {
  phase: ContentImportPhase;
  /** Display only; the `File` itself stays in the component (a reducer never holds a handle). */
  fileName: string | null;
  /** Set exactly in `confirm` — the `409`'s `details`, which persisted nothing. */
  mismatch: ContentHandleMismatchDetails | null;
  /** Set exactly in `done`. */
  result: ContentImportResponse | null;
  /** Set exactly in `error` — the server's message verbatim. */
  errorMessage: string | null;
  /** Whether the next POST must carry `confirm=1`; set only by an approved handle clash. */
  confirmHandle: boolean;
};

export const INITIAL_CONTENT_IMPORT_STATE: ContentImportState = {
  phase: 'idle',
  fileName: null,
  mismatch: null,
  result: null,
  errorMessage: null,
  confirmHandle: false,
};

export type ContentImportAction =
  | { type: 'pick'; fileName: string }
  | { type: 'rejected'; message: string }
  | { type: 'submit' }
  | { type: 'succeeded'; result: ContentImportResponse }
  | { type: 'mismatch'; details: ContentHandleMismatchDetails }
  | { type: 'failed'; message: string }
  /** The handle clash was approved — re-post the same file with `confirm=1`. */
  | { type: 'approve' }
  /** The handle clash was declined — the file stays picked, nothing was persisted. */
  | { type: 'decline' }
  | { type: 'reset' };

export function contentImportReducer(
  state: ContentImportState,
  action: ContentImportAction,
): ContentImportState {
  switch (action.type) {
    case 'pick':
      // A new file wipes the previous outcome *and* any confirmation given for the old one.
      return { ...INITIAL_CONTENT_IMPORT_STATE, phase: 'ready', fileName: action.fileName };
    case 'rejected':
      // Refused before it ever left the browser (wrong extension, over the cap).
      return {
        ...INITIAL_CONTENT_IMPORT_STATE,
        phase: 'error',
        errorMessage: action.message,
      };
    case 'submit':
      if (state.fileName === null) return state;
      if (state.phase !== 'ready' && state.phase !== 'error') return state;
      return { ...state, phase: 'uploading', errorMessage: null, mismatch: null };
    case 'succeeded':
      return {
        ...state,
        phase: 'done',
        result: action.result,
        mismatch: null,
        errorMessage: null,
        confirmHandle: false,
      };
    case 'mismatch':
      return { ...state, phase: 'confirm', mismatch: action.details, errorMessage: null };
    case 'failed':
      return { ...state, phase: 'error', errorMessage: action.message, mismatch: null };
    case 'approve':
      if (state.phase !== 'confirm') return state;
      return { ...state, phase: 'uploading', mismatch: null, confirmHandle: true };
    case 'decline':
      if (state.phase !== 'confirm') return state;
      return { ...state, phase: 'ready', mismatch: null, confirmHandle: false };
    case 'reset':
      return { ...INITIAL_CONTENT_IMPORT_STATE };
  }
}

/** The Import button is live only with a picked file and no request in flight. */
export function canSubmitContentImport(state: ContentImportState): boolean {
  return state.fileName !== null && (state.phase === 'ready' || state.phase === 'error');
}

/**
 * The `409`'s `details`, narrowed from `ApiError.details` (typed `unknown` by the transport).
 *
 * Pure and status-first rather than error-first, so it stays testable in the node suite and this
 * module keeps importing nothing but `@lw/shared`: the dialog hands it `error.status` and
 * `error.details`. A `409` that is *not* a handle clash (or one missing the new handle, which is
 * the only field the confirmation sentence cannot do without) returns `null` and falls through to
 * the ordinary error path — never to a confirm dialog asking about `@undefined`.
 */
export function parseContentHandleMismatch(
  status: number,
  details: unknown,
): ContentHandleMismatchDetails | null {
  if (status !== 409 || typeof details !== 'object' || details === null) return null;
  const source = details as Record<string, unknown>;
  if (source.reason !== 'handle_mismatch') return null;
  if (typeof source.newHandle !== 'string' || source.newHandle.length === 0) return null;
  const existingHandles = Array.isArray(source.existingHandles)
    ? source.existingHandles.filter((handle): handle is string => typeof handle === 'string')
    : [];
  return { reason: 'handle_mismatch', newHandle: source.newHandle, existingHandles };
}

/**
 * The handle-clash question. The server sends the two handles, not the sentence (its `409` carries
 * `details`, not a message), so the words are the client's — the values never are.
 */
export function contentHandleMismatchMessage(details: ContentHandleMismatchDetails): string {
  const existing = details.existingHandles.map((handle) => `@${handle}`).join(', ');
  const known = existing.length > 0 ? existing : 'a different account';
  return `This export belongs to @${details.newHandle}; existing imports are ${known}. Import anyway?`;
}

/**
 * Client-side pre-flight for the picked file: the two failures worth catching before pushing
 * 50 MB over the wire. Returns the refusal, or `null` when the file may be sent.
 */
export function validateContentImportFile(file: { name: string; size: number }): string | null {
  if (!/\.zip$/i.test(file.name)) return 'Pick the .zip archive the extension produced.';
  if (file.size > CONTENT_MAX_UPLOAD_BYTES) {
    return `That archive is larger than the ${String(
      Math.floor(CONTENT_MAX_UPLOAD_BYTES / (1024 * 1024)),
    )} MB import limit.`;
  }
  if (file.size === 0) return 'That file is empty.';
  return null;
}
